import { useEffect, useState } from "react";
import { Unlink, Loader2, Send } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

/** Weekly note to each owner about their own dead images and links — a list of
 *  700 findings belongs to nobody, so nobody fixes it. */
export default function KbLinkDigestCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/kb/link-digest").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSaving(true);
    try {
      const { data } = await api.put("/kb/link-digest", {
        enabled: next.enabled, max_rows: Number(next.max_rows) || 20,
        include_unverifiable: !!next.include_unverifiable,
      });
      setCfg((c) => ({ ...c, ...data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/kb/link-digest/send-now");
      toast.success(data.sent
        ? `Sent to ${data.sent} owner${data.sent === 1 ? "" : "s"} covering ${data.findings} findings`
        : data.reason || "Nothing broken right now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the digest");
    } finally { setSending(false); }
  };

  if (!cfg) return null;
  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="kb-link-digest-card">
      <div className="flex items-center gap-2">
        <Unlink className="h-4 w-4 text-slate-500" />
        <h2 className="font-heading text-base font-semibold text-slate-800">Broken image &amp; link digest</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Every Monday morning each review owner gets one email listing only the images and links
        in their own articles that no longer resolve. Anything with no owner goes to the KB
        default owner in a single summary.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ enabled: v })}
            disabled={!cfg.can_edit} data-testid="kb-link-digest-enabled" />
          Weekly digest on
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          Max rows per email
          <Input type="number" min={5} max={100} value={cfg.max_rows}
            onChange={(e) => setCfg({ ...cfg, max_rows: e.target.value })}
            onBlur={() => save({})} disabled={!cfg.can_edit}
            className="w-20" data-testid="kb-link-digest-rows" />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={!!cfg.include_unverifiable}
            onCheckedChange={(v) => save({ include_unverifiable: v })}
            disabled={!cfg.can_edit} data-testid="kb-link-digest-unverifiable" />
          Include links we couldn&apos;t check
        </label>
        <Button variant="outline" onClick={sendNow} disabled={!cfg.can_edit || sending}
          data-testid="kb-link-digest-send">
          {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
          Send now
        </Button>
        {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <p className="mt-2 text-xs text-slate-500" data-testid="kb-link-digest-summary">
        {(cfg.owners || []).map((o) => `${o.name}: ${o.count}`).join(" · ") || "Nothing broken in any owned article"}
        {cfg.unowned ? ` · ${cfg.unowned} in articles with no owner` : ""}
        {cfg.last_sent_at ? ` · last sent ${String(cfg.last_sent_at).slice(0, 10)}` : ""}
      </p>
    </section>
  );
}
